import { HttpClient } from '@angular/common/http';
import { inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { BehaviorSubject, finalize, Observable } from 'rxjs';
import { EnvironmentToken } from '../../app.config';
import { Inspection, InspectionItem } from '../models/inspection.model';

export class InspectionService {
  #http = inject(HttpClient);
  #apiUrl = inject(EnvironmentToken).apiUrl;
  #isLoading = signal<Set<unknown>>(new Set());

  #inspections = new BehaviorSubject<Inspection[]>([]);

  public inspections$ = this.#inspections.asObservable();
  public inspections = toSignal(this.inspections$, {
    initialValue: [],
  });

  #inspectionItems = new BehaviorSubject<InspectionItem[]>([]);

  public inspectionItems$ = this.#inspectionItems.asObservable();
  public inspectionItems = toSignal(this.inspectionItems$, {
    initialValue: [],
  });

  getInspections() {
    this.#execute(
      this.#http.get<Inspection[]>(`${this.#apiUrl}/api/inspections`),
    ).subscribe({
      next: (inspections: any) => {
        this.#inspections.next(inspections.data ?? inspections);
      },
      error: (e: any) => console.log(e),
    });
  }

  updateInspection(data: Partial<Inspection>, id: number) {
    return this.#execute(
      this.#http.put<Partial<Inspection>[]>(
        `${this.#apiUrl}/api/inspections/` + id,
        data,
      ),
    );
  }
  createInspection(data: Partial<Inspection>) {
    return this.#execute(
      this.#http.post<Partial<Inspection>[]>(
        `${this.#apiUrl}/api/inspections`,
        data,
      ),
    );
  }

  deleteInspection(id: number) {
    return this.#execute(
      this.#http.delete(`${this.#apiUrl}/api/inspections/` + id),
    );
  }

  getPotentialInspectionItems(itemType: string) {
    this.#execute(
      this.#http.get<Partial<InspectionItem>[]>(
        `${this.#apiUrl}/api/inspections/items?type=${itemType}`,
      ),
    ).subscribe({
      next: (inspectionItems: any) => {
        this.#inspectionItems.next(inspectionItems);
      },
      error: (e: any) => console.log(e),
    });
  }

  checkItem(itemId: number) {
    return this.#execute(
      this.#http.patch(`${this.#apiUrl}/api/inspections/items/${itemId}/check`, {}),
    );
  }

  getOverduePsas() {
    return this.#execute(
      this.#http.get<Partial<InspectionItem>[]>(
        `${this.#apiUrl}/api/inspections/items?type=psa&filter=overdue`,
      ),
    );
  }

  #execute<T = unknown>(apiCall: Observable<T>) {
    this.#isLoading.update((x) => {
      x.add(apiCall);
      return new Set(x.values());
    });
    return apiCall.pipe(
      finalize(() =>
        this.#isLoading.update((x) => {
          x.delete(apiCall);
          return new Set(x.values());
        }),
      ),
    );
  }
}
